/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Programs.css';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config';
import { getSelectedYear, isReadOnly } from '../config/academicYear';

// Türkçe karakterleri İngilizce karakterlere çevir
const turkishToEnglish = (text) => {
  if (!text) return '';

  const turkishChars = {
    'ç': 'c', 'Ç': 'C',
    'ğ': 'g', 'Ğ': 'G',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ş': 's', 'Ş': 'S',
    'ü': 'u', 'Ü': 'U'
  };

  return text.replace(/[çÇğĞıİöÖşŞüÜ]/g, letter => turkishChars[letter] || letter);
};

function Programs({ faculty, userType }) {
  const navigate = useNavigate();
  const selectedAcademicYearValue = getSelectedYear();
  const selectedAcademicYear = /^\d{4}$/.test(selectedAcademicYearValue)
    ? Number.parseInt(selectedAcademicYearValue, 10)
    : null;
  const [programs, setPrograms] = useState([]);
  const [newProgram, setNewProgram] = useState('');
  const [newDurationYear, setNewDurationYear] = useState('');
  const [editingProgram, setEditingProgram] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploadStatuses, setUploadStatuses] = useState({});
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [coursesPlan, setCoursesPlan] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseUploadStatuses, setCourseUploadStatuses] = useState({});
  const [downloadingAll, setDownloadingAll] = useState(false);

  const fetchPrograms = useCallback(async () => {
    try {
      const endpoint = userType === 'admin' ? `${API_BASE_URL}/api/programs` : `${API_BASE_URL}/api/public/programs`;
      const response = await fetch(`${endpoint}?faculty_id=${faculty.id}`);
      const data = await response.json();
      setPrograms(data);

      // Her program için Excel dosyası kontrolü yap
      const excelPrograms = new Set();
      for (const program of data) {
        try {
          const coursesEndpoint = userType === 'admin' ? `${API_BASE_URL}/api/programs` : `${API_BASE_URL}/api/public/programs`;
          const coursesResponse = await fetch(`${coursesEndpoint}/${program.id}/courses`);
          if (coursesResponse.ok) {
            const courses = await coursesResponse.json();
            if (courses.length > 0) {
              excelPrograms.add(program.id);
            }
          }
        } catch (error) {
          console.error(`Error checking courses for program ${program.id}:`, error);
        }
      }

    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  }, [faculty?.id, userType]);

  useEffect(() => {
    if (faculty?.id) {
      fetchPrograms();
    }
  }, [faculty?.id, userType, fetchPrograms]);

  const handleAddProgram = async (e) => {
    e.preventDefault();
    if (!newProgram || !newDurationYear) return;
    if (selectedAcademicYear === null) {
      alert('Aktif akademik yıl yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/programs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newProgram,
          faculty_id: faculty.id,
          duration_year: parseInt(newDurationYear),
          academic_year: selectedAcademicYear
        }),
      });

      if (response.ok) {
        setNewProgram('');
        setNewDurationYear('');
        setShowAddForm(false);
        fetchPrograms();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Program eklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error adding program:', error);
      alert('Program eklenirken bir hata oluştu');
    }
  };

  const handleEditProgram = async (e) => {
    e.preventDefault();
    if (!editingProgram) return;
    if (selectedAcademicYear === null) {
      alert('Aktif akademik yıl yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/programs/${editingProgram.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editingProgram.name,
          duration_year: parseInt(editingProgram.duration_year),
          academic_year: selectedAcademicYear
        }),
      });

      if (response.ok) {
        setEditingProgram(null);
        fetchPrograms();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Program güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error updating program:', error);
      alert('Program güncellenirken bir hata oluştu');
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Bu programı silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchPrograms();
      }
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  const startEditing = (program) => {
    setEditingProgram({ ...program });
  };

  const cancelEditing = () => {
    setEditingProgram(null);
  };

  const toggleAddForm = () => {
    setShowAddForm(!showAddForm);
    if (!showAddForm) {
      setNewProgram('');
      setNewDurationYear('');
    }
  };

  const handleFileSelect = async (event, programId) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.xlsx')) {
      if (isReadOnly()) {
        setUploadStatuses(prev => ({ ...prev, [programId]: 'Arşiv akademik yılına ders yüklenemez' }));
        event.target.value = '';
        return;
      }
      const academicYear = getSelectedYear();
      if (!academicYear) {
        setUploadStatuses(prev => ({ ...prev, [programId]: 'Önce akademik yıl seçin' }));
        event.target.value = '';
        return;
      }

      setUploadStatuses(prev => ({ ...prev, [programId]: 'Yükleniyor...' }));

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/upload-excel`, {
          method: 'POST',
          headers: { 'X-Academic-Year': academicYear },
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          setUploadStatuses(prev => ({ ...prev, [programId]: 'Dosya başarıyla yüklendi' }));

          event.target.value = '';
        } else {
          setUploadStatuses(prev => ({ ...prev, [programId]: `Hata: ${data.error}` }));
        }
      } catch (error) {
        console.error('Dosya yükleme hatası:', error);
        setUploadStatuses(prev => ({ ...prev, [programId]: 'Dosya yüklenirken bir hata oluştu' }));
      }
    } else {
      setUploadStatuses(prev => ({ ...prev, [programId]: 'Lütfen geçerli bir Excel (.xlsx) dosyası seçin' }));
    }
  };

  const slugify = (text) => {
    if (!text) return '';
    return String(text)
      .toLowerCase()
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleProgramClick = (programId) => {
    const program = programs.find(p => p.id === programId);
    const slug = slugify(program ? program.name : String(programId));
    try { if (program) window.sessionStorage.setItem('selectedProgram', JSON.stringify(program)); } catch (e) { }
    navigate(`/program/${slug}/ders-plani-icerik?pid=${programId}`, { state: { program } });
  };

  const handleCourseClick = (e, programId, courseCode) => {
    e.stopPropagation();
    // Ders detayları sayfasına yönlendir
    navigate(`/course/${programId}/${courseCode}`);
  };

  const handleCourseWordUpload = async (event, programId, courseCode) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.docx')) {
      setCourseUploadStatuses(prev => ({ ...prev, [`${programId}-${courseCode}`]: 'Yükleniyor...' }));

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/upload-word`, {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        if (response.ok) {
          setCourseUploadStatuses(prev => ({ ...prev, [`${programId}-${courseCode}`]: 'Dosya başarıyla yüklendi' }));
          event.target.value = '';

          // Ders detaylarını yeniden yükle
          // Bu satır gereksiz, kaldırıyoruz
        } else {
          setCourseUploadStatuses(prev => ({ ...prev, [`${programId}-${courseCode}`]: `Hata: ${data.error}` }));
        }
      } catch (error) {
        console.error('Word dosyası yükleme hatası:', error);
        setCourseUploadStatuses(prev => ({ ...prev, [`${programId}-${courseCode}`]: 'Dosya yüklenirken bir hata oluştu' }));
      }
    } else {
      setCourseUploadStatuses(prev => ({ ...prev, [`${programId}-${courseCode}`]: 'Lütfen geçerli bir Word (.docx) dosyası seçin' }));
    }
  };

  const handleCourseDelete = async (programId, courseCode) => {
    if (window.confirm(`${courseCode} dersinin Word dosyasından gelen verilerini silmek istediğinizden emin misiniz? Ders kaydı korunacak, sadece Word verileri silinecek.`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/courses/${courseCode}/delete`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (response.ok) {
          alert('Word dosyası verileri başarıyla silindi! Ders kaydı korundu.');
          // Ders listesini yeniden yükle
          fetchPrograms();
        } else {
          alert(`Silme hatası: ${data.error}`);
        }
      } catch (error) {
        console.error('Silme hatası:', error);
        alert('Silme işlemi sırasında hata oluştu!');
      }
    }
  };

  const downloadAllCoursesAsZip = async (courses, programId, userType) => {
    setDownloadingAll(true);
    try {
      const baseEndpoint = userType === 'admin' ? `${API_BASE_URL}/api/programs` : `${API_BASE_URL}/api/public/programs`;
      const zip = new JSZip();

      // Fakülte ve bölüm adını çek
      let facultyName = faculty && faculty.name ? faculty.name : 'Fakulte';
      let programName = 'Bolum';
      try {
        const programRes = await fetch(`${baseEndpoint}?faculty_id=${faculty.id}`);
        const programs = programRes.ok ? await programRes.json() : [];
        const currentProgram = programs.find(p => p.id === programId);
        if (currentProgram) {
          programName = currentProgram.name;
        }
      } catch (e) { }

      for (const course of courses) {
        // Tüm verileri paralel olarak çek
        const [courseDetailsRes, courseResourcesRes, learningOutcomesRes, weeklyTopicsRes, programOutcomesRes, aktsActivitiesRes, sustainableDevelopmentGoalsRes, evaluationSystemRes, teachingStaffRes, programLearningOutcomesRes] = await Promise.all([
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/course-details`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/course-resources`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/learning-outcomes`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/weekly-topics`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/program-outcomes`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/akts-activities`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/sustainable-development-goals`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/evaluation-system`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/teaching-staff`),
          fetch(`${baseEndpoint}/${programId}/courses/${course.course_code}/program-outcomes`)
        ]);

        const courseDetails = courseDetailsRes.ok ? await courseDetailsRes.json() : {};
        const courseResources = courseResourcesRes.ok ? await courseResourcesRes.json() : { main_resources: '', supplementary_resources: '' };
        const learningOutcomes = learningOutcomesRes.ok ? await learningOutcomesRes.json() : [];
        const weeklyTopics = weeklyTopicsRes.ok ? await weeklyTopicsRes.json() : [];
        const programOutcomes = programOutcomesRes.ok ? await programOutcomesRes.json() : [];
        const aktsActivities = aktsActivitiesRes.ok ? await aktsActivitiesRes.json() : [];
        const sustainableDevelopmentGoals = sustainableDevelopmentGoalsRes.ok ? await sustainableDevelopmentGoalsRes.json() : [];
        const evaluationSystem = evaluationSystemRes.ok ? await evaluationSystemRes.json() : [];
        const teachingStaff = teachingStaffRes.ok ? await teachingStaffRes.json() : [];
        const programLearningOutcomes = programLearningOutcomesRes.ok ? await programLearningOutcomesRes.json() : [];

        // HTML içeriği oluştur
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <style>
                @page {
                  size: A4 portrait;
                  margin: 15mm;
                }
                body { 
                  font-family: 'Times New Roman', serif; 
                  margin: 0; 
                  padding: 0; 
                  color: #2c3e50;
                  line-height: 1.5;
                  font-size: 14pt;
                  max-width: 100%;
                }
                .header { 
                  text-align: center; 
                  margin-bottom: 30px; 
                  border-bottom: 3px solid #2c3e50;
                  padding-bottom: 20px;
                  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
                  color: white;
                  padding: 20px;
                  border-radius: 8px;
                  margin-bottom: 25px;
                }
                .logo { 
                  width: 180px; 
                  height: 135px; 
                  margin-bottom: 15px;
                  filter: brightness(0) invert(1);
                }
                .title { 
                  font-size: 36px; 
                  font-weight: bold; 
                  color: white; 
                  margin: 10px 0; 
                  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
                }
                .subtitle { 
                  font-size: 18px; 
                  color: #ecf0f1; 
                  margin-bottom: 10px; 
                  font-style: italic;
                }
                .info-table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 20px 0; 
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                .info-table th, .info-table td { 
                  border: 1px solid #bdc3c7; 
                  padding: 16px; 
                  text-align: left; 
                  font-size: 14pt;
                }
                .info-table th { 
                  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
                  color: white; 
                  font-weight: bold; 
                  font-size: 15pt;
                }
                .info-table tr:nth-child(even) { 
                  background-color: #f8f9fa; 
                }
                .info-table tr:hover {
                  background-color: #e8f4f8;
                }
                .section-title { 
                  font-size: 24px; 
                  font-weight: bold; 
                  color: #2c3e50; 
                  margin: 25px 0 15px 0; 
                  border-bottom: 2px solid #3498db;
                  padding-bottom: 8px;
                  text-transform: uppercase;
                  letter-spacing: 1px;
                }
                .content-table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 15px 0; 
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                .content-table th, .content-table td { 
                  border: 1px solid #bdc3c7; 
                  padding: 14px; 
                  text-align: left; 
                  font-size: 13pt;
                }
                .content-table th { 
                  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
                  color: white; 
                  font-weight: bold; 
                  font-size: 14pt;
                }
                .content-table tr:nth-child(even) { 
                  background-color: #f8f9fa; 
                }
                .content-table tr:hover {
                  background-color: #e8f4f8;
                }
                .text-content { 
                  margin: 20px 0; 
                  line-height: 1.6; 
                  text-align: justify;
                  font-size: 14pt;
                  padding: 15px;
                  background-color: #f8f9fa;
                  border-left: 4px solid #3498db;
                  border-radius: 4px;
                }
                .page-break {
                  page-break-before: always;
                }
                .section-title {
                  font-size: 20px;
                  font-weight: bold;
                  color: #2c3e50;
                  margin: 30px 0 15px 0;
                  padding: 10px 0;
                  border-bottom: 2px solid #3498db;
                  text-transform: uppercase;
                }
                .footer {
                  text-align: center;
                  margin-top: 30px;
                  padding-top: 20px;
                  border-top: 1px solid #bdc3c7;
                  color: #7f8c8d;
                  font-size: 10pt;
                }
              </style>
            </head>
            <body>
              <div class="header">
                <img src="/kostu_logo.png" class="logo" alt="KOSTÜ Logo">
                <div class="title">${course.course_code} - ${course.course_name}</div>
                <div class="subtitle">KOCAELİ SAĞLIK VE TEKNOLOJİ ÜNİVERSİTESİ</div>
              </div>
              
              <table class="info-table">
                <thead>
                  <tr><th>Özellik</th><th>Değer</th></tr>
                </thead>
                <tbody>
                  <tr><td>Ders Kodu</td><td>${courseDetails.course_code || course.course_code}</td></tr>
                  <tr><td>Ders Adı</td><td>${courseDetails.course_name || course.course_name}</td></tr>
                  <tr><td>Program</td><td>${courseDetails.program_name || 'Belirtilmemiş'}</td></tr>
                  <tr><td>Dönem</td><td>${courseDetails.donem || course.donem}</td></tr>
                  <tr><td>Ders Tipi</td><td>${courseDetails.course_type || course.course_type || 'Belirtilmemiş'}</td></tr>
                  <tr><td>Teorik</td><td>${courseDetails.teorik || course.teorik} saat</td></tr>
                  <tr><td>Uygulama</td><td>${courseDetails.uygulama || course.uygulama} saat</td></tr>
                  <tr><td>Laboratuvar</td><td>${courseDetails.laboratuvar || course.laboratuvar} saat</td></tr>
                  <tr><td>Kredi</td><td>${courseDetails.kredi || course.kredi}</td></tr>
                  <tr><td>AKTS</td><td>${courseDetails.akts || course.akts}</td></tr>
                  <tr><td>Dil</td><td>${courseDetails.course_language || 'Belirtilmemiş'}</td></tr>
                  <tr><td>Koordinatör</td><td>${courseDetails.coordinator || 'Belirtilmemiş'}</td></tr>
                  ${courseDetails.on_kosul_dersleri ? `<tr><td>Ön Koşul</td><td>${courseDetails.on_kosul_dersleri}</td></tr>` : ''}
                </tbody>
              </table>
              
              ${learningOutcomes.length > 0 ? `
                <div class="section-title">Öğrenme Çıktıları</div>
                <table class="content-table">
                  <thead>
                    <tr><th>No</th><th>Öğrenme Çıktısı</th></tr>
                  </thead>
                  <tbody>
                    ${learningOutcomes.map(outcome => `
                      <tr><td style="text-align: center; width: 40px;">${outcome.number}</td><td>${outcome.name}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${weeklyTopics.length > 0 ? `
                <div class="section-title">Haftalık Konular</div>
                <table class="content-table">
                  <thead>
                    <tr><th>Hafta</th><th>Konu</th><th>Ön Hazırlık</th><th>Metodoloji</th></tr>
                  </thead>
                  <tbody>
                    ${weeklyTopics.map(topic => `
                      <tr>
                        <td style="text-align: center; width: 80px;">${topic.number}. Hafta</td>
                        <td>${topic.topic}</td>
                        <td>${topic.on_hazirlik || '-'}</td>
                        <td>${topic.metodoloji || '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${programOutcomes.length > 0 ? `
                <div class="section-title">Program Çıktıları</div>
                <table class="content-table">
                  <thead>
                    <tr><th>No</th><th>Program Çıktısı</th></tr>
                  </thead>
                  <tbody>
                    ${programOutcomes.map(outcome => `
                      <tr><td style="text-align: center; width: 40px;">${outcome.number}</td><td>${outcome.name}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${courseDetails.content ? `
                <div class="section-title">Ders Açıklaması</div>
                <div class="text-content">${courseDetails.content}</div>
              ` : ''}
              
              ${courseDetails.aim ? `
                <div class="section-title">Ders Amaçları</div>
                <div class="text-content">${courseDetails.aim}</div>
              ` : ''}
              
              ${courseResources.main_resources ? `
                <div class="section-title">Ana Kaynaklar</div>
                <div class="text-content">${courseResources.main_resources}</div>
              ` : ''}
              
              ${courseResources.supplementary_resources ? `
                <div class="section-title">Yardımcı Kaynaklar</div>
                <div class="text-content">${courseResources.supplementary_resources}</div>
              ` : ''}
              
              ${aktsActivities.length > 0 ? `
                <div class="section-title">AKTS Etkinlikleri</div>
                <table class="content-table">
                  <thead>
                    <tr><th>Etkinlik Adı</th><th>Sayı</th><th>Süre (Saat)</th><th>Toplam İş Yükü</th></tr>
                  </thead>
                  <tbody>
                    ${aktsActivities.map(activity => `
                      <tr><td>${activity.activity_name}</td><td>${activity.count}</td><td>${activity.duration_hours}</td><td>${activity.total_workload}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${evaluationSystem.length > 0 ? `
                <div class="section-title">Değerlendirme Sistemi</div>
                <table class="content-table">
                  <thead>
                    <tr><th>Etkinlik Adı</th><th>Sayı</th><th>Katkı Payı (%)</th></tr>
                  </thead>
                  <tbody>
                    ${evaluationSystem.map(item => `
                      <tr><td>${item.activity_name}</td><td>${item.count}</td><td>${item.contribution_share}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${teachingStaff.length > 0 ? `
                <div class="section-title">Öğretim Elemanları</div>
                <table class="content-table">
                  <thead>
                    <tr><th>Unvan</th><th>Ad Soyad</th><th>E-Posta</th><th>Görevi</th></tr>
                  </thead>
                  <tbody>
                    ${teachingStaff.map(staff => `
                      <tr><td>${staff.title}</td><td>${staff.first_name} ${staff.last_name}</td><td>${staff.email || ''}</td><td>${staff.duty}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${sustainableDevelopmentGoals.length > 0 ? `
                <div class="section-title">Sürdürülebilir Kalkınma Amaçları</div>
                <table class="content-table">
                  <thead>
                    <tr><th>No</th><th>Amaç Açıklaması</th><th>KATKI DÜZEYİ</th></tr>
                  </thead>
                  <tbody>
                    ${sustainableDevelopmentGoals.map(goal => `
                      <tr><td style="text-align: center; width: 40px;">${goal.goal_number}</td><td>${goal.goal_description}</td><td>${goal.contribution_levels ? goal.contribution_levels.join(', ') : ''}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
              
              ${programLearningOutcomes.length > 0 ? `
                <div class="section-title">Program Öğrenme Çıktıları</div>
                <table class="content-table">
                  <thead>
                    <tr><th>Sıra No</th><th>Program Öğrenme Çıktıları</th><th>KATKI DÜZEYİ</th></tr>
                  </thead>
                  <tbody>
                    ${programLearningOutcomes.map((outcome, index) => `
                      <tr><td style="text-align: center; width: 40px;">${index + 1}</td><td>${outcome.outcome_description}</td><td>${outcome.contribution_levels ? outcome.contribution_levels.join(', ') : ''}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
              ` : ''}
          
          <div class="footer">
            <p>Bu belge Kocaeli Sağlık ve Teknoloji Üniversitesi tarafından hazırlanmıştır.</p>
            <p>Oluşturulma Tarihi: ${new Date().toLocaleDateString('tr-TR')}</p>
          </div>
        </body>
        </html>
      `;

        // Geçici container oluştur
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = '210mm';
        tempContainer.style.backgroundColor = 'white';
        tempContainer.style.padding = '15mm';
        tempContainer.style.boxSizing = 'border-box';
        tempContainer.style.fontFamily = 'Times New Roman, serif';
        tempContainer.style.fontSize = '14pt';
        tempContainer.style.lineHeight = '1.5';
        tempContainer.style.color = '#2c3e50';
        tempContainer.style.overflow = 'visible';
        tempContainer.style.height = 'auto';
        tempContainer.style.minHeight = '297mm';

        // HTML'i geçici container'a ekle
        tempContainer.innerHTML = htmlContent;
        document.body.appendChild(tempContainer);

        // Container'ın gerçek yüksekliğini al
        const containerHeight = tempContainer.scrollHeight;
        const containerWidth = tempContainer.scrollWidth;

        // HTML'i canvas'a çevir
        const canvas = await html2canvas(tempContainer, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: containerWidth,
          height: containerHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: containerWidth,
          windowHeight: containerHeight
        });

        // Geçici container'ı kaldır
        document.body.removeChild(tempContainer);

        // Canvas'ı PDF'e çevir
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');

        const imgWidth = 210; // A4 genişliği
        const pageHeight = 295; // A4 yüksekliği (kenar boşlukları hariç)
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        // İlk sayfa
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Ek sayfalar
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        const pdfBlob = pdf.output('blob');
        zip.file(`${turkishToEnglish(course.course_code)}-${turkishToEnglish(course.course_name)}.pdf`, pdfBlob);
      }

      // ZIP'i indir
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFileName = `${turkishToEnglish(facultyName)}-${turkishToEnglish(programName)}.zip`;
      saveAs(zipBlob, zipFileName);
    } catch (error) {
      console.error('Toplu indirme hatası:', error);
    } finally {
      setDownloadingAll(false);
    }
  };

  if (!faculty) return null;

  return (
    <div className="programs-section">
      <div className="programs-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
        >
          ← Geri
        </button>
        <h2>{faculty.name} - Programlar</h2>
      </div>

      {userType === 'admin' && (
        <div className="section-header">
          <button
            onClick={toggleAddForm}
            className={`toggle-button ${showAddForm ? 'active' : ''}`}
          >
            {showAddForm ? 'Program Ekleme Formunu Kapat' : 'Program Ekle'}
          </button>
        </div>
      )}

      {userType === 'admin' && showAddForm && !editingProgram && (
        <form onSubmit={handleAddProgram} className="add-form">
          <div className="form-group">
            <input
              type="text"
              value={newProgram}
              onChange={(e) => setNewProgram(e.target.value)}
              placeholder="Program Adı"
              className="program-name-input"
            />
            <input
              type="number"
              value={newDurationYear}
              onChange={(e) => setNewDurationYear(e.target.value)}
              placeholder="Süre (Yıl)"
              className="duration-input"
            />
            <input
              type="number"
              value={selectedAcademicYear ?? ''}
              readOnly
              placeholder="Akademik Yıl"
              className="academic-year-input"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <button type="submit">Kaydet</button>
            <button type="button" onClick={toggleAddForm} className="cancel-button">İptal</button>
          </div>
        </form>
      )}

      {userType === 'admin' && editingProgram && (
        <form onSubmit={handleEditProgram} className="edit-form">
          <div className="form-group">
            <input
              type="text"
              value={editingProgram.name}
              onChange={(e) => setEditingProgram({ ...editingProgram, name: e.target.value })}
              className="program-name-input"
            />
            <input
              type="number"
              value={editingProgram.duration_year}
              onChange={(e) => setEditingProgram({ ...editingProgram, duration_year: e.target.value })}
              className="duration-input"
            />
            <input
              type="number"
              value={selectedAcademicYear ?? ''}
              readOnly
              className="academic-year-input"
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <button type="submit">Güncelle</button>
            <button type="button" onClick={cancelEditing} className="cancel-button">İptal</button>
          </div>
        </form>
      )}

      <div className="programs-list">
        {programs.map((program) => (
          <div key={program.id} className="program-item">
            <div
              className={`program-header ${selectedProgramId === program.id ? 'selected' : ''}`}
              onClick={() => handleProgramClick(program.id)}
            >
              <div className="program-info">
                <h3>{program.name}</h3>
                <p>Eğitim Süresi: {program.duration_year} Yıl</p>
              </div>

              {userType === 'admin' && (
                <div className="program-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const slug = slugify(program.name);
                      navigate(`/program/${slug}/syllabus-status`, { state: { program } });
                    }}
                    className="edit-button"
                    style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                  >
                    İzlence Doluluk
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing(program);
                    }}
                    className="edit-button"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProgram(program.id);
                    }}
                    className="delete-button"
                  >
                    Sil
                  </button>
                </div>
              )}
            </div>

            {userType === 'admin' && (
              <div className="upload-section">
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => handleFileSelect(e, program.id)}
                  style={{ display: 'none' }}
                  id={`excel-upload-${program.id}`}
                />
                <label htmlFor={`excel-upload-${program.id}`} className="upload-button">
                  Excel Dosyası Yükle
                </label>
                {uploadStatuses[program.id] && (
                  <span className="upload-status">{uploadStatuses[program.id]}</span>
                )}
              </div>
            )}

            {/* Program sayfasına yönlendirildiği için burada dersler açılmıyor */}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Programs; 
